import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Bot, Plus, Trash2, KeyRound, Loader2, CheckCircle2, XCircle, Image as ImgIcon, Play, Shield, Ticket, FileUp, Sparkles, Rocket, X } from 'lucide-react';

const PLATFORM_KINDS = [
  { value: 'esafad', label: 'esafad (carlonovello.esafad.it)' },
  { value: 'opn',    label: 'O.P.N. — Organismo Paritetico Nazionale (whitelabel esafad)' },
  { value: 'lcr',    label: 'LCR Formazione (piattaforma.lcrformazione.com)' },
  { value: 'ipconsulenza', label: 'IP Consulenza Formativa' },
  { value: 'generic', label: 'Altro (generico)' },
];

export default function PlatformsPage() {
  const [platforms, setPlatforms] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openEnroll, setOpenEnroll] = useState(null);
  const [form, setForm] = useState({ nickname: '', kind: 'esafad', base_url: '', username: '', password: '', notes: '', courses: '' });
  const [discente, setDiscente] = useState({ nome: '', cognome: '', codice_fiscale: '', email: '', telefono: '', data_nascita: '' });
  const [dryRun, setDryRun] = useState(true);
  // v1.0.1: Iscrizione Massiva da Unilav — UI matrix
  const [unilavFiles, setUnilavFiles] = useState([]); // File[] selezionati
  const [unilavExtracting, setUnilavExtracting] = useState(false);
  const [matrix, setMatrix] = useState([]); // [{discente, platform_id, course_names: []}]
  const [matrixDryRun, setMatrixDryRun] = useState(true);
  const [matrixEnrolling, setMatrixEnrolling] = useState(false);
  const [matrixResult, setMatrixResult] = useState(null);
  const fileInputRef = useRef(null);
  // LCR codes viewer state
  const [lcrCodesOpen, setLcrCodesOpen] = useState(false);
  const [lcrCodesPlatform, setLcrCodesPlatform] = useState(null);
  const [lcrCodesLoading, setLcrCodesLoading] = useState(false);
  const [lcrCodesData, setLcrCodesData] = useState(null);

  const fetchLcrCodes = async (p) => {
    setLcrCodesPlatform(p);
    setLcrCodesOpen(true);
    setLcrCodesData(null);
    setLcrCodesLoading(true);
    try {
      const { data } = await api.get(`/platforms/${p.id}/lcr-available-codes`, { timeout: 120000 });
      setLcrCodesData(data);
    } catch (e) {
      toast.error('Errore recupero codici LCR: ' + (e?.response?.data?.detail || e.message));
      setLcrCodesData({ total: 0, by_course: [] });
    } finally {
      setLcrCodesLoading(false);
    }
  };
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [p, j] = await Promise.all([api.get('/platforms'), api.get('/platforms/jobs?limit=20')]);
      setPlatforms(p.data || []);
      setJobs(j.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.nickname || !form.username) { toast.error('Nickname e username obbligatori'); return; }
    setSubmitting(true);
    try {
      // v1.0.1: courses è un textarea con una riga per corso
      const coursesList = (form.courses || '')
        .split(/\r?\n/)
        .map(s => s.trim())
        .filter(Boolean);
      const payload = { ...form, courses: coursesList };
      if (editing) {
        await api.put(`/platforms/${editing.id}`, payload);
        toast.success('Aggiornata');
      } else {
        if (!form.password) { toast.error('Password obbligatoria al primo inserimento'); setSubmitting(false); return; }
        await api.post('/platforms', payload);
        toast.success('Piattaforma creata — credenziali cifrate AES');
      }
      setOpenCreate(false); setEditing(null);
      setForm({ nickname: '', kind: 'esafad', base_url: '', username: '', password: '', notes: '', courses: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setSubmitting(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare definitivamente "${p.nickname}"?`)) return;
    try { await api.delete(`/platforms/${p.id}`); toast.success('Eliminata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      nickname: p.nickname,
      kind: p.kind,
      base_url: p.base_url,
      username: '',
      password: '',
      notes: p.notes || '',
      courses: (p.courses || []).join('\n'),
    });
    setOpenCreate(true);
  };

  // v1.0.1: Iscrizione Massiva da Unilav
  const onUnilavFiles = (e) => {
    const fs = Array.from(e.target.files || []).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (fs.length === 0) { toast.error('Seleziona almeno un PDF Unilav'); return; }
    if (fs.length > 30) { toast.error('Massimo 30 file per volta'); return; }
    setUnilavFiles(fs);
    setMatrix([]);
    setMatrixResult(null);
  };

  const extractUnilav = async () => {
    if (unilavFiles.length === 0) { toast.error('Carica almeno un PDF Unilav'); return; }
    setUnilavExtracting(true);
    try {
      const fd = new FormData();
      unilavFiles.forEach(f => fd.append('files', f));
      const r = await api.post('/ai/bulk-enrollment/extract-unilav-pdf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240000,
      });
      const oks = (r.data.results || []).filter(x => x.ok && x.data);
      if (oks.length === 0) {
        toast.error('Nessun discente estratto. Controlla che i PDF siano Unilav leggibili.');
        return;
      }
      // Pre-popola la matrice: 1 riga per discente, piattaforma default = prima disponibile, corsi vuoti
      const defaultPlatform = platforms[0]?.id || '';
      const rows = oks.map(o => ({
        discente: o.data,
        platform_id: defaultPlatform,
        course_names: [''], // 1 dropdown vuoto di default
      }));
      setMatrix(rows);
      toast.success(`Estratti ${oks.length} discenti da ${unilavFiles.length} PDF`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore estrazione AI: ' + e.message);
    } finally { setUnilavExtracting(false); }
  };

  const updateMatrixRow = (idx, patch) => {
    setMatrix(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const setMatrixCourse = (rowIdx, courseIdx, value) => {
    setMatrix(rows => rows.map((r, i) => {
      if (i !== rowIdx) return r;
      const cn = [...r.course_names];
      cn[courseIdx] = value;
      return { ...r, course_names: cn };
    }));
  };
  const addMatrixCourse = (rowIdx) => {
    setMatrix(rows => rows.map((r, i) => i === rowIdx ? { ...r, course_names: [...r.course_names, ''] } : r));
  };
  const removeMatrixCourse = (rowIdx, courseIdx) => {
    setMatrix(rows => rows.map((r, i) => {
      if (i !== rowIdx) return r;
      const cn = r.course_names.filter((_, j) => j !== courseIdx);
      return { ...r, course_names: cn.length ? cn : [''] };
    }));
  };
  const removeMatrixRow = (rowIdx) => {
    setMatrix(rows => rows.filter((_, i) => i !== rowIdx));
  };

  const submitMatrix = async () => {
    // Validazione
    const cleanRows = matrix
      .map(r => ({ ...r, course_names: r.course_names.map(c => c.trim()).filter(Boolean) }))
      .filter(r => r.platform_id && r.course_names.length > 0);
    if (cleanRows.length === 0) {
      toast.error('Seleziona almeno una piattaforma e un corso per riga'); return;
    }
    const totalJobs = cleanRows.reduce((sum, r) => sum + r.course_names.length, 0);
    if (!matrixDryRun && !window.confirm(`⚠️ INVIO REALE: stai per avviare ${totalJobs} iscrizioni REALI sulle piattaforme. Procedere?`)) {
      return;
    }
    setMatrixEnrolling(true);
    try {
      const r = await api.post('/platforms/enroll-from-matrix', {
        rows: cleanRows,
        dry_run: matrixDryRun,
      });
      setMatrixResult(r.data);
      toast.success(`Avviati ${r.data.jobs_total} job ${matrixDryRun ? 'DRY-RUN' : 'REALI'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio iscrizioni');
    } finally { setMatrixEnrolling(false); }
  };

  const startEnrollment = async () => {
    if (!discente.nome || !discente.cognome || !discente.codice_fiscale) {
      toast.error('Nome, cognome e codice fiscale obbligatori'); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/platforms/enroll', {
        platform_id: openEnroll.id,
        discente,
        dry_run: dryRun,
      });
      toast.success(`Job avviato (${dryRun ? 'DRY-RUN' : 'INVIO REALE'})`);
      setOpenEnroll(null);
      setDiscente({ nome: '', cognome: '', codice_fiscale: '', email: '', telefono: '', data_nascita: '' });
      // Poll for ~60s
      let polls = 0;
      const tk = setInterval(async () => {
        polls++;
        try {
          const jr = await api.get(`/platforms/jobs/${r.data.job_id}`);
          if (jr.data.status !== 'running') {
            clearInterval(tk);
            if (jr.data.status === 'success') toast.success(`✅ ${jr.data.message || 'Completato'}`);
            else toast.error(`❌ ${jr.data.message || 'Errore'}`);
            load();
          } else if (polls > 30) { clearInterval(tk); load(); }
        } catch { clearInterval(tk); }
      }, 3000);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio job');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="platforms-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Bot className="w-7 h-7 text-emerald-700" /> Agente Iscrizioni Piattaforme
          </h1>
          <p className="text-sm text-slate-600 mt-1 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Credenziali cifrate AES — visibili solo a te (admin proprietario).
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ nickname: '', kind: 'esafad', base_url: '', username: '', password: '', notes: '', courses: '' }); setOpenCreate(true); }} data-testid="new-platform-btn">
          <Plus className="w-4 h-4 mr-1" /> Aggiungi piattaforma
        </Button>
      </motion.div>

      {/* Platforms */}
      <div>
        <h2 className="text-lg font-semibold mb-2">Piattaforme configurate</h2>
        {loading ? <p>Caricamento…</p> : platforms.length === 0 ? (
          <div className="bg-white p-8 rounded-lg text-center border" data-testid="empty-platforms">
            <Bot className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-600">Nessuna piattaforma configurata.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {platforms.map(p => (
              <div key={p.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`platform-${p.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold">{p.nickname}</h3>
                  <Badge variant="outline">{p.kind}</Badge>
                </div>
                <p className="text-xs text-slate-500 truncate" title={p.base_url}>{p.base_url}</p>
                <p className="text-xs mt-1"><KeyRound className="w-3 h-3 inline mr-1" />{p.username_masked} {p.password_set && <CheckCircle2 className="w-3 h-3 inline text-emerald-500" />}</p>
                {p.notes && <p className="text-xs text-slate-500 mt-2 italic">{p.notes}</p>}
                <div className="flex gap-1 mt-3">
                  <Button size="sm" className="flex-1" onClick={() => { setOpenEnroll(p); setDryRun(true); }} data-testid={`enroll-${p.id}`}>
                    <Play className="w-3.5 h-3.5 mr-1" /> Iscrivi
                  </Button>
                  {p.kind === 'lcr' && (
                    <Button size="sm" variant="outline" onClick={() => fetchLcrCodes(p)} data-testid={`lcr-codes-${p.id}`} title="Vedi codici tutor disponibili">
                      <Ticket className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>Modifica</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p)} data-testid={`delete-platform-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* === v1.0.1: Iscrizione Massiva da Unilav === */}
      <Card className="border-violet-200 bg-gradient-to-br from-violet-50/40 to-white" data-testid="unilav-bulk-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Rocket className="w-5 h-5 text-violet-600" /> Iscrizione Massiva da Unilav
            <Badge variant="secondary" className="ml-2">AI</Badge>
          </CardTitle>
          <p className="text-sm text-slate-600">
            Carica i Unilav, l'AI estrae i dipendenti, scegli piattaforma + corsi per ciascuno, il bot iscrive tutti.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1: Upload PDFs */}
          {matrix.length === 0 && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                multiple
                onChange={onUnilavFiles}
                className="hidden"
                data-testid="unilav-file-input"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-violet-300 rounded-lg p-6 text-center cursor-pointer hover:bg-violet-50 transition-colors"
                data-testid="unilav-drop-area"
              >
                <FileUp className="w-10 h-10 mx-auto text-violet-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">Clicca o trascina qui i PDF Unilav</p>
                <p className="text-xs text-slate-500 mt-1">PDF • max 30 file • L'AI estrae i dati anagrafici</p>
              </div>
              {unilavFiles.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="unilav-file-list">
                  <p className="text-xs text-slate-600 font-medium">{unilavFiles.length} file pronti:</p>
                  {unilavFiles.slice(0, 6).map((f, i) => (
                    <p key={i} className="text-xs text-slate-500 truncate">• {f.name} ({(f.size / 1024).toFixed(0)} KB)</p>
                  ))}
                  {unilavFiles.length > 6 && <p className="text-xs text-slate-400">…e altri {unilavFiles.length - 6}</p>}
                  <Button
                    onClick={extractUnilav}
                    disabled={unilavExtracting || platforms.length === 0}
                    className="bg-violet-600 hover:bg-violet-700 mt-2"
                    data-testid="unilav-extract-btn"
                  >
                    {unilavExtracting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Estrazione AI in corso (60-180s)…</> : <><Sparkles className="w-4 h-4 mr-2" /> Estrai {unilavFiles.length} discenti</>}
                  </Button>
                  {platforms.length === 0 && (
                    <p className="text-xs text-red-600 mt-1">⚠️ Aggiungi prima almeno una piattaforma sopra.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Matrix table */}
          {matrix.length > 0 && (
            <div className="space-y-3" data-testid="unilav-matrix">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  {matrix.length} discenti estratti — assegna piattaforma e corsi:
                </p>
                <Button variant="ghost" size="sm" onClick={() => { setMatrix([]); setUnilavFiles([]); setMatrixResult(null); }} data-testid="unilav-reset">
                  <X className="w-4 h-4 mr-1" /> Ricomincia
                </Button>
              </div>

              <div className="space-y-2">
                {matrix.map((row, idx) => {
                  const selectedPlatform = platforms.find(p => p.id === row.platform_id);
                  const availableCourses = selectedPlatform?.courses || [];
                  return (
                    <div key={idx} className="border rounded-lg p-3 bg-white" data-testid={`matrix-row-${idx}`}>
                      <div className="flex items-start gap-3 flex-wrap">
                        {/* Discente info */}
                        <div className="min-w-[200px] flex-1">
                          <p className="font-semibold text-sm text-slate-800">
                            {row.discente.cognome} {row.discente.nome}
                          </p>
                          <p className="text-xs text-slate-500 font-mono">{row.discente.codice_fiscale}</p>
                          {row.discente.azienda_ragione_sociale && (
                            <p className="text-xs text-slate-400 italic truncate" title={row.discente.azienda_ragione_sociale}>
                              {row.discente.azienda_ragione_sociale}
                            </p>
                          )}
                        </div>

                        {/* Platform dropdown */}
                        <div className="min-w-[180px]">
                          <Label className="text-xs">Piattaforma</Label>
                          <Select
                            value={row.platform_id}
                            onValueChange={(v) => updateMatrixRow(idx, { platform_id: v, course_names: [''] })}
                          >
                            <SelectTrigger data-testid={`matrix-platform-${idx}`}>
                              <SelectValue placeholder="Scegli…" />
                            </SelectTrigger>
                            <SelectContent>
                              {platforms.map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.nickname} <span className="text-xs text-slate-400 ml-1">({p.kind})</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Course dropdowns */}
                        <div className="flex-1 min-w-[280px]">
                          <Label className="text-xs">Corsi da iscrivere</Label>
                          <div className="space-y-1.5">
                            {row.course_names.map((cn, cIdx) => (
                              <div key={cIdx} className="flex gap-1.5 items-center">
                                <Select
                                  value={cn}
                                  onValueChange={(v) => setMatrixCourse(idx, cIdx, v)}
                                >
                                  <SelectTrigger data-testid={`matrix-course-${idx}-${cIdx}`} className="flex-1">
                                    <SelectValue placeholder={availableCourses.length === 0 ? '(nessun corso configurato)' : 'Scegli corso…'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableCourses.length === 0 ? (
                                      <SelectItem value="__none__" disabled>
                                        Aggiungi corsi nella piattaforma →
                                      </SelectItem>
                                    ) : availableCourses.map(c => (
                                      <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {row.course_names.length > 1 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeMatrixCourse(idx, cIdx)}
                                    className="h-9 px-2 text-red-600"
                                    data-testid={`matrix-course-remove-${idx}-${cIdx}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            {availableCourses.length > row.course_names.length && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addMatrixCourse(idx)}
                                className="h-7 text-xs"
                                data-testid={`matrix-add-course-${idx}`}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Aggiungi corso
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Remove row */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMatrixRow(idx)}
                          className="text-slate-400 hover:text-red-600"
                          title="Rimuovi discente"
                          data-testid={`matrix-remove-row-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Submit */}
              <div className="flex items-center gap-3 flex-wrap pt-3 border-t">
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded p-2">
                  <Checkbox checked={matrixDryRun} onCheckedChange={setMatrixDryRun} data-testid="matrix-dry-run" />
                  <Label className="text-sm cursor-pointer" onClick={() => setMatrixDryRun(!matrixDryRun)}>
                    <strong>DRY-RUN</strong>: solo screenshot, NON sottomette
                    {!matrixDryRun && <span className="text-red-700 font-bold"> ⚠️ INVIO REALE!</span>}
                  </Label>
                </div>
                <Button
                  onClick={submitMatrix}
                  disabled={matrixEnrolling}
                  className={matrixDryRun ? 'bg-violet-600 hover:bg-violet-700' : 'bg-red-700 hover:bg-red-800'}
                  data-testid="matrix-submit-btn"
                >
                  {matrixEnrolling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                  Iscrivi tutti
                </Button>
              </div>

              {matrixResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm" data-testid="matrix-result">
                  <p className="font-semibold text-emerald-700">
                    ✓ Avviati {matrixResult.jobs_total} job. Vedi tabella "Ultimi job" sotto per il monitoraggio.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Jobs */}
      {jobs.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-2">Ultimi job ({jobs.length})</h2>
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                <tr><th className="text-left p-2">Piattaforma</th><th className="text-left p-2">Discente</th><th className="text-left p-2">Modalità</th><th className="text-left p-2">Stato</th><th className="text-left p-2">Avviato</th><th className="text-left p-2">Note</th></tr>
              </thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id} className="border-t" data-testid={`job-${j.id}`}>
                    <td className="p-2 font-medium">{j.platform_kind}</td>
                    <td className="p-2 text-xs">{j.discente_summary?.cognome} {j.discente_summary?.nome}<br/><span className="text-slate-400">{j.discente_summary?.cf}</span></td>
                    <td className="p-2"><Badge variant={j.dry_run ? 'outline' : 'default'}>{j.dry_run ? 'DRY-RUN' : 'REALE'}</Badge></td>
                    <td className="p-2">
                      {j.status === 'success' && <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-3 h-3 mr-1" />OK</Badge>}
                      {j.status === 'error' && <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Errore</Badge>}
                      {j.status === 'running' && <Badge className="bg-blue-100 text-blue-800"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running</Badge>}
                    </td>
                    <td className="p-2 text-xs text-slate-500">{j.started_at ? new Date(j.started_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                    <td className="p-2 text-xs">
                      <div className="max-w-[300px] truncate" title={j.message}>{j.message || ''}</div>
                      {j.has_screenshot && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/platforms/jobs/${j.id}/screenshot`} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs"><ImgIcon className="w-3 h-3 inline" /> screenshot</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dialog: create/edit platform */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica piattaforma' : 'Nuova piattaforma'}</DialogTitle>
            <DialogDescription>Le credenziali sono cifrate AES nel DB e visibili solo a te.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nickname *</Label><Input value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} placeholder="es. esafad Limoncini" data-testid="platform-nickname" /></div>
            <div>
              <Label>Piattaforma *</Label>
              <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="platform-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORM_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Base URL *</Label><Input value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="https://carlonovello.esafad.it" data-testid="platform-url" /></div>
            <div><Label>Username *</Label><Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} data-testid="platform-username" /></div>
            <div><Label>Password {editing ? '(lascia vuoto per non cambiare)' : '*'}</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="platform-password" /></div>
            <div>
              <Label>Corsi disponibili (uno per riga)</Label>
              <Textarea
                rows={4}
                value={form.courses}
                onChange={e => setForm({ ...form, courses: e.target.value })}
                placeholder={"HACCP Primo Rilascio 12h\nAntincendio Rischio Basso\nFormazione Generale Sicurezza 4h"}
                data-testid="platform-courses"
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">Useranno questa lista i dropdown nella sezione "Iscrizione Massiva da Unilav".</p>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Annulla</Button>
            <Button onClick={submit} disabled={submitting} data-testid="platform-submit">{submitting ? 'Invio…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: enroll */}
      <Dialog open={!!openEnroll} onOpenChange={(o) => !o && setOpenEnroll(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Iscrivi discente — {openEnroll?.nickname}</DialogTitle>
            <DialogDescription>Compila i dati del discente. L'agente fa login e compila il form sulla piattaforma.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nome *</Label><Input value={discente.nome} onChange={e => setDiscente({ ...discente, nome: e.target.value })} data-testid="enroll-nome" /></div>
              <div><Label>Cognome *</Label><Input value={discente.cognome} onChange={e => setDiscente({ ...discente, cognome: e.target.value })} data-testid="enroll-cognome" /></div>
            </div>
            <div><Label>Codice Fiscale *</Label><Input value={discente.codice_fiscale} onChange={e => setDiscente({ ...discente, codice_fiscale: e.target.value.toUpperCase() })} data-testid="enroll-cf" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Email</Label><Input value={discente.email} onChange={e => setDiscente({ ...discente, email: e.target.value })} /></div>
              <div><Label>Telefono</Label><Input value={discente.telefono} onChange={e => setDiscente({ ...discente, telefono: e.target.value })} /></div>
            </div>
            <div><Label>Data nascita (gg/mm/aaaa)</Label><Input value={discente.data_nascita} onChange={e => setDiscente({ ...discente, data_nascita: e.target.value })} placeholder="01/01/1980" /></div>
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded p-2 mt-2">
              <input type="checkbox" id="dry-run" checked={dryRun} onChange={e => setDryRun(e.target.checked)} data-testid="dry-run-checkbox" />
              <label htmlFor="dry-run" className="text-sm">
                <strong>Modalità DRY-RUN</strong>: compila il form ma NON sottomette (no costi).
                {!dryRun && <span className="text-red-700 font-bold"> ⚠️ Disattivata: invio REALE!</span>}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEnroll(null)}>Annulla</Button>
            <Button onClick={startEnrollment} disabled={submitting} className={dryRun ? '' : 'bg-red-700 hover:bg-red-800'} data-testid="enroll-submit">
              {submitting ? 'Avvio…' : (dryRun ? 'Avvia DRY-RUN' : 'AVVIA INVIO REALE')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- LCR Codici Tutor Dialog --- */}
      <Dialog open={lcrCodesOpen} onOpenChange={setLcrCodesOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-amber-600" />
              Codici Tutor Disponibili — {lcrCodesPlatform?.nickname}
            </DialogTitle>
            <DialogDescription>
              Codici raggruppati per corso. "Validato? = No" = ancora utilizzabili. Operazione lunga (~30s, login + scraping).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {lcrCodesLoading && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-600 mb-2" />
                <p className="text-sm text-slate-600">Login + scraping in corso…</p>
              </div>
            )}
            {!lcrCodesLoading && lcrCodesData && lcrCodesData.total === 0 && (
              <div className="text-center py-6 text-slate-500">
                <p>Nessun codice disponibile (o login fallito).</p>
                <p className="text-xs mt-1">Verifica credenziali della piattaforma.</p>
              </div>
            )}
            {!lcrCodesLoading && lcrCodesData && lcrCodesData.total > 0 && (
              <>
                <p className="text-sm text-slate-700"><strong>{lcrCodesData.total}</strong> codici disponibili in <strong>{lcrCodesData.by_course.length}</strong> corsi.</p>
                {lcrCodesData.by_course.map((g, i) => (
                  <div key={i} className="border rounded-lg p-3 bg-slate-50">
                    <p className="text-sm font-semibold text-slate-800 mb-2">{g.corso} <Badge variant="outline" className="ml-1">{g.codici.length}</Badge></p>
                    <div className="flex flex-wrap gap-1">
                      {g.codici.map((c, j) => (
                        <button
                          key={j}
                          onClick={() => { navigator.clipboard.writeText(c); toast.success('Codice copiato negli appunti'); }}
                          className="font-mono text-xs bg-white border border-slate-300 rounded px-2 py-1 hover:bg-blue-50 hover:border-blue-400 transition-colors"
                          title="Click per copiare"
                          data-testid={`lcr-code-${c}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLcrCodesOpen(false)}>Chiudi</Button>
            {lcrCodesPlatform && (
              <Button onClick={() => fetchLcrCodes(lcrCodesPlatform)} disabled={lcrCodesLoading} variant="secondary">
                {lcrCodesLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Ricarica
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
